
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Renderer2, DOCUMENT } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TYPE_MENU, TYPE_SECTIONS, TYPES_MENU, VISIBILITY_ITEM } from 'app/core/models/constans.model';
import { AnalyticsService } from 'app/core/services/analytics.service';
import { DbService } from 'app/core/services/db.service';
import { Restaurant } from 'app/menu/models/restaurants.model';
import * as moment from 'moment';
import { COLLECTIONS_BD } from '../../../core/models/collections.model';
import { Advertisements } from '../../models/advertisements.model';
import { Menu } from '../../models/menu.model';
import { MenuStorageService } from '../../services/menu-storage.service';
import { MenuService } from '../../services/menu.service';
import { PreloadImgComponent } from './../../../core/directives/preload-img/preload-img.component';
import { Item, SectionMenu } from './../../models/menu.model';

/**
 * Componente que muestra el menu del restaurante
 */
@Component({
    selector: 'app-menu-detail',
    templateUrl: './menu-detail.component.html',
    styleUrls: ['./menu-detail.component.scss'],
    standalone: false
})
export class MenuDetailComponent extends PreloadImgComponent implements OnInit, OnDestroy {
  /**
   * Informacion del menu seleccionado
   */
  menu: Menu = {} as Menu;
  /**
   * Fecha actual
   */
  date = moment(new Date()).format('YYYY-MM-DD');
  /**
   * Informacion de la publicidad activa
   */
  ads: Advertisements = {} as Advertisements;
  /**
   * Indica si se visualiza o no la publicidad
   */
  isAdsOn = true;
  /**
   * Selector de idioma
   */
  language = 'ES';
  /**
   * Identificador del restaurante
   */
  id = '';
  /**
   * indica el tipo de menu del restaurante
   */
  menuType = '';
  /**
   * Indica que tab esta seleccionado
   */
  tabIndexSelected = 0;
  /**
   * Informacion del restaurante actual
   */
  restaurant: Restaurant = {} as Restaurant;
  /**
   * Indica si de debe visualizar el footer
   */
  footer = false;
  /**
   * Indica si los tabs estaran anclados al incio de la pagina
   */
  isSticky = false;
  px = 500;
  /**
   * Tipos de secciones
   */
  readonly TYPE_SECTIONS = TYPE_SECTIONS;
  /**
   * Tipos de menus
   */
  readonly TYPES_MENU = TYPES_MENU;
  /**
   * Momento (epoch ms) en que el usuario entro a ver el menu,
   * usado para calcular cuanto tiempo paso viendolo (menu_time_spent)
   */
  private sessionStartTime = 0;
  /**
   * Evita enviar el evento menu_time_spent mas de una vez por sesion
   */
  private timeSpentSent = false;
  /**
   * Constructor
   *
   * @param db conexion con BD
   * @param route ruta actual
   * @param router enrutador
   * @param menuStorage servicio de menu
   */
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private db: DbService,
    private route: ActivatedRoute,
    private router: Router,
    private menuStorage: MenuStorageService,
    private element: ElementRef,
    public menuService: MenuService,
    private analyticsService: AnalyticsService
  ) {
    super();
    if (this.route.snapshot.params.lang) {
      this.menuService.setLang(this.route.snapshot.params.lang.toUpperCase());
    }
  }

  /**
   * Se encarga de indicar si se hizo scroll en el menu
   * y poner fixed los tabs o el filtrado de secciones
   */
  validateScroll() {
    this.menuStorage.scroll$.subscribe(scroll => {
      if (scroll >= this.px) {
        const matTabHeader: any = document.getElementsByClassName('mat-tab-header');
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let index = 0; index < matTabHeader.length; index++) {
          matTabHeader[index].style.backgroundColor = this.menu.color;
          matTabHeader[index].style.backgroundImage = `url(${this.menu.background})`;
        }
        this.isSticky = true;
      } else {
        this.isSticky = false;
      }
    });
  }


  /**
   * Inicializa la informacion y valida si hay publicidades
   */
  ngOnInit(): void {
    this.menuService.setSideMenu(false);
    if (!this.menu.background) {
      this.menu.background = '';
    }
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.id = this.route.snapshot.params.id;
    this.menuService.getLang().subscribe((lan: string) => {
      this.language = lan;

    });
    this.getRestaurant();
    this.validateMetrics();
    this.validateScroll();
    this.sessionStartTime = Date.now();
  }

  ngOnDestroy(): void {
    this.trackTimeSpent();
  }

  /**
   * Detecta cuando el usuario oculta/cambia de pestaña para registrar
   * cuanto tiempo paso viendo el menu (metrica menu_time_spent)
   */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.trackTimeSpent();
    }
  }

  /**
   * Envia el evento menu_time_spent con la duracion de la sesion actual,
   * una sola vez. Descarta duraciones absurdas (pestaña dormida en
   * segundo plano, no uso real) o negativas/cero.
   */
  private trackTimeSpent(): void {
    if (this.timeSpentSent || !this.sessionStartTime) {
      return;
    }
    const durationSeconds = Math.round((Date.now() - this.sessionStartTime) / 1000);
    const maxReasonableSeconds = 2 * 60 * 60;
    if (durationSeconds <= 0 || durationSeconds > maxReasonableSeconds) {
      return;
    }
    this.timeSpentSent = true;
    const info = {
      restaurant_id: this.route.snapshot.params.id,
      menu_type: this.route.snapshot.params.type,
      type: 'menu_time_spent',
      product_id: '',
      duration_seconds: durationSeconds,
    };
    this.analyticsService.sendAnalyticsInfo('menu_time_spent', info);
  }

  /**
   * Registra una vista del menu (metrica page_view), a diferencia de
   * first_visit esto se envia SIEMPRE (no se deduplica por dispositivo) -
   * es lo que permite mostrar "visitas totales" por rango de fechas en el
   * admin, ya que el contador de .../metrics (visits) es un acumulado de
   * toda la vida, no filtrable por fecha. También es la señal que se usa
   * para la métrica de horas pico de tráfico.
   */
  private trackPageView(): void {
    const info = {
      restaurant_id: this.route.snapshot.params.id,
      menu_type: this.route.snapshot.params.type,
      type: 'page_view',
      product_id: ''
    };
    this.analyticsService.sendAnalyticsInfo('page_view', info);
  }

  validateIsFirstVisit() {
    const info = {
      menu_type: this.route.snapshot.params.type,
      restaurant_id: this.route.snapshot.params.id,
      client_id: this.route.snapshot.params.id,
      type: 'first_visit',
      product_id: ''
    };
    const isFirstTimeUser = !localStorage.getItem('firstTimeUser');
    if (isFirstTimeUser) {
      this.analyticsService.sendAnalyticsInfo('first_visit', info).then(() => {
      });
      localStorage.setItem('firstTimeUser', JSON.stringify([this.id]));
    } else {
      const e = localStorage.getItem('firstTimeUser');
      if (e) {
        const restaurants = JSON.parse(e);
        const exist = restaurants.find((restaurant: string) => restaurant === this.id);
        if (!exist) {
          this.analyticsService.sendAnalyticsInfo('first_visit', info).then(() => {
          });
          restaurants.push(this.id);
          localStorage.setItem('firstTimeUser', JSON.stringify(restaurants));
        }
      }
    }
  }
  /**
   * Filtra el contenido por una seccion seleccionada
   *
   * @param section seccion a filtrar
   */
  filterSection(section: string): void {
    setTimeout(() => {
      const sectionSelect = document.getElementById(section + 'section');
      if (sectionSelect) {
        sectionSelect.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }, 200);
  }

  /**
   * Obtiene la informacion del restaurante actual
   */
  getRestaurant(): void {
    this.menuStorage.getRestaurant().subscribe((r: Restaurant) => {
      if (!r || !this.restaurant.name) {
        this.setRestaurant();
      } else {
        this.restaurant = r;
        this.menuType = r.menuType;
        if (r.language) {
          this.menuService.setLang(r.language);
          this.language = r.language;
        }
      }
    });
    setTimeout(() => {
      this.loadInfo();
    }, 100);
  }

  /**
   * Carga la informacion del menu dependiendo
   * el departamento seleccionado
   * Bar, cocina, ambos
   */
  loadInfo(): void {
    this.db.get(`${COLLECTIONS_BD.restaurants}/${this.route.snapshot.params.id}/menu`,
      ['departament', 'in', [this.route.snapshot.params.type, 'imagesMenu']])
      .subscribe((menu: Menu[]) => {
        if (this.menu.shape) {
          this.px = 180;
        };
        let menuRestaurant: Menu = {} as Menu;
        if (this.menuType === 'imagesMenu') {
          let type = 'imagesMenu';
          if (this.route.snapshot.params.type === 'imagesMenuEn' || this.route.snapshot.params.type === 'imagesMenuEs') {
            type = this.route.snapshot.params.type;
          }
          const exist = menu.find((m: Menu) => m.departament === type);
          if (exist) {
            menuRestaurant = exist;
          }
        } else {
          const exist = menu.find((m: Menu) => (m.departament === this.route.snapshot.params.type));
          if (exist) {
            menuRestaurant = exist;
          }
        }
        const menuFiltered = menuRestaurant.sections.map((r: SectionMenu) => {
          let items: Item[] = [];
          if (r.type === TYPE_SECTIONS.drinks || r.type === TYPE_SECTIONS.drinks_midle || r.type === TYPE_SECTIONS.wines) {
            items = r.items.filter((s: Item) => ((s.typeMenu === VISIBILITY_ITEM.restaurantAndDelivery ||
              s.typeMenu === VISIBILITY_ITEM.restaurant || !s.typeMenu) &&
              (s.availableBotle === 'S' || s.availableDrink === 'S' || s.comment === 'separator')));
          } else {
            items = r.items.filter((s: Item) => ((s.typeMenu === VISIBILITY_ITEM.restaurantAndDelivery ||
              s.typeMenu === VISIBILITY_ITEM.restaurant || !s.typeMenu) && s.available === 'S'));
          }
          items = items.map((i: any) => ({ ...i, section: r.name }));
          return {
            ...r,
            items,
            photo: r.photo ? r.photo : undefined,
          };
        }
        );
        let sectionsMenu = menuFiltered.filter((s: SectionMenu) =>
        ((s.items.length) > 0 ||
          (s.items.length === 0 && s.type === TYPE_SECTIONS.separator && menuRestaurant.shape === 'scroll')));
        sectionsMenu = sectionsMenu.filter((section: SectionMenu) => !section.isInactive);
        menuRestaurant.sections = sectionsMenu;
        setTimeout(() => {
          this.menu = menuRestaurant;

          this.menuStorage.setMenu(menuRestaurant);
          this.setColors();
          if (this.menu.shape === TYPES_MENU.tabs || !this.menu.shape) {
            this.menu.sections.map((section: SectionMenu) => ({
              ...section,
              name: section.name.toUpperCase(),
              nameEn: section.nameEn.toUpperCase(),
            }));
            this.renderer.removeClass(this.document.body, 'embedded-body');
          }
          this.setColors();
          this.setColorsFilterSections();
          if (this.route.snapshot.params.section) {
            this.goToSection();
          }
        }, 200);
      });
  }

  /**
   * Intercambia el menu entre cocina y bebidas
   */
  changeMenu(): void {
    if (this.route.snapshot.params.type === 'cocina') {
      this.router.navigateByUrl(`menu-detail/${this.route.snapshot.params.id}/bar`);
    } else {
      this.router.navigateByUrl(`menu-detail/${this.route.snapshot.params.id}/cocina`);
    }
  }

  tabChange(index?: number): void {
    if (window.innerWidth < 760) {
      document.body.scrollTop = 0;
    }
    if (index !== undefined) {
      this.trackSectionView(index);
    }
  }

  /**
   * Registra que seccion/tab del menu se esta viendo (metrica section_view).
   * El indice recibido es el de las secciones visibles en los tabs, es
   * decir el mismo listado filtrado que usa el template
   * (menu.sections sin las de tipo separator).
   *
   * @param index indice de la seccion seleccionada
   */
  private trackSectionView(index: number): void {
    const visibleSections = (this.menu.sections || []).filter((s) => s.type !== TYPE_SECTIONS.separator);
    const section = visibleSections[index];
    if (!section) {
      return;
    }
    const info = {
      restaurant_id: this.route.snapshot.params.id,
      menu_type: this.route.snapshot.params.type,
      type: 'section_view',
      product_id: section.name,
    };
    this.analyticsService.sendAnalyticsInfo('section_view', info);
  }



  /**
   * Navega al la pagina anterior
   */
  back(): void {
    if (this.restaurant.home) {
      this.menuService.setHome(false);
    }
    this.router.navigateByUrl(`/menu/${this.route.snapshot.params.id}`);
  }

  /**
   * Intercambia los valores del lenguaje seleccionado
   */
  selectLanguage(): void {
    if (this.language === 'ES') {
      this.language = 'EN';
      this.menuService.setLang('EN');
    } else {
      this.menuService.setLang('ES');
      this.language = 'ES';
    }
  }

  /**
   * Apaga la publicidad en pantalla
   *
   */
  adsOff(isOn: boolean): void {
    this.isAdsOn = isOn;
    this.setColors();
  }

  /**
   * Indica si el menu es de imagenes o no
   *
   * @returns boolean
   */
  isMenuImages(): boolean {
    if (this.menuType === TYPE_MENU.imagesMenu ||
      this.menuType === TYPE_MENU.imagesMenuEn ||
      this.menuType === TYPE_MENU.imagesMenuEs) {
      return true;
    }
    return false;
  }

  /**
   * Setea el valor del sidemenu
   * si esta en desktop siempre debe ser true
   */
  setShowMenu() {
    this.menuService.getIsMobile().subscribe((value) => {
      if (value) {
        this.menuService.setSideMenu(false);
      } else {
        this.menuService.setSideMenu(true);
      }
    });
  }

  /**
   * Indica si se debe visualizar o no el
   * sidemenu
   */
  isVisibleSideMenu(): boolean {
    if (this.menu && !this.isAdsOn && (!this.isMenuImages())) {
      if (this.menuType === TYPE_MENU.oneMenu && !this.restaurant.home) {
        return false;
      }
      return true;
    }
    return false;
  }
  /**
   * Establece los colores del
   * matTabGroup segun lo establecido
   * en el menu seleccionado
   */
  private setColors(): void {
    setTimeout(() => {
      const arrows: any = document.getElementsByClassName('mat-tab-header-pagination-chevron');
      const tabLabels: any = document.getElementsByClassName('mat-tab-label-content');
      const underLines: any = document.getElementsByClassName('mat-ink-bar ');
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < underLines.length; index++) {
        underLines[index].style.backgroundColor = this.menu.colorText;
      }
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < tabLabels.length; index++) {
        tabLabels[index].style.color = this.menu.colorText;
        tabLabels[index].style.fontFamily = this.menu.fontSections ? this.menu.fontSections : this.menu.fontHeader;
      }
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < arrows.length; index++) {
        arrows[index].style.borderColor = this.menu.colorText;
      }
      if (this.menu.shape === TYPES_MENU.scroll) {
        this.setColorsFilterSections();
      }
    }, 400);
    setTimeout(() => {
      this.footer = true;
    }, 1500);
  }

  /**
   * Establece los colores del
   * filtro de secciones segun lo establecido
   * en el menu seleccionado
   */
  private setColorsFilterSections(): void {
    setTimeout(() => {
      const outline: any = document.getElementsByClassName('mat-form-field-outline');
      const label: any = document.getElementsByClassName('mat-form-field-label');
      const category: any = document.getElementsByClassName('mat-select-value');
      const arrow: any = document.getElementsByClassName('mat-select-arrow');

      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < category.length; index++) {
        category[index].style.color = this.menu.colorText + '!important';
      }
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < label.length; index++) {
        label[index].style.color = this.menu.colorText;
      }
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < outline.length; index++) {
        outline[index].style.color = this.menu.colorText;
      }
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let index = 0; index < arrow.length; index++) {
        arrow[index].style.color = this.menu.colorText;
      }
    }, 400);
  }


  /**
   * Obtiene la informacion del restaurante actual
   */
  private setRestaurant(): void {
    this.db.findOne(COLLECTIONS_BD.restaurants, this.route.snapshot.params.id).subscribe((r: Restaurant) => {
      this.menuType = r.menuType;
      if (r.language) {
        this.language = r.language;
        this.menuService.setLang(r.language);
      }
      this.restaurant = r;
      if (this.restaurant.metrics) {
        this.validateIsFirstVisit();
        this.trackPageView();
      }
      this.restaurant.id = this.route.snapshot.params.id;
      this.menuStorage.setRestaurant(this.restaurant);
    });
  }

  /**
   * Hace la animacion de scroll horizontal en los tabs
   * de las secciones del menu actual
   */
  private setAnimations(index: number): void {
    if (!this.isMenuImages()) {
      setTimeout(() => {
        const tabsContainer = this.element.nativeElement.querySelector('.mat-tab-list');
        let cont = 0;
        let loop = 0;
        const interval = setInterval(() => {
          cont += 100;
          loop += 1;
          if (tabsContainer) {
            tabsContainer.scroll({
              left: cont,
              behavior: 'smooth',
            });
          }
          if (loop === index) {
            this.tabIndexSelected = index;
            clearInterval(interval);
          }
        }, 500);
      }, 1000);
    }
  }

  /**
   * Valida si el restaurante tiene activas las metricas y si tiene
   * coleccion de metricas creadas si la tiene incrementa en 1 las visitas
   * si no la tiene la crea e inicializa las visitas en 1
   */
  private validateMetrics(): void {
    const metricsSubscribe = this.db.get(`${COLLECTIONS_BD.restaurants}/${this.route.snapshot.params.id}/metrics`)
      .subscribe((metrics: any[]) => {
        if (this.restaurant.metrics) {
          if (metrics.length > 0) {
            metrics[0].visits = metrics[0].visits + 1;
            this.db.update(`${COLLECTIONS_BD.restaurants}/${this.route.snapshot.params.id}/metrics`,
              metrics[0].id, { visits: metrics[0].visits });
          } else {
            this.db.add(`${COLLECTIONS_BD.restaurants}/${this.route.snapshot.params.id}/metrics`, { visits: 1 });
          }
        }
        metricsSubscribe.unsubscribe();
      });
  }

  /**
   * Navega a la seccion seleccionada
   */
  private goToSection() {
    const section = this.route.snapshot.params.section;

    switch (this.menu.shape) {
      case TYPES_MENU.buttons:
        // Lo controlo desde el componente de buttons
        break;
      case TYPES_MENU.scroll:
        setTimeout(() => {
          this.filterSection(this.menu.sections[parseInt(section, 10)].name);
        }, 2000);
        break;
      case TYPES_MENU.tabs:
        this.tabIndexSelected = parseInt(section, 10);
        this.tabChange();
        this.setAnimations(this.tabIndexSelected);
        break;
      case TYPES_MENU.pdf:
        // Lo controlo desde el componente de pdf
        break;
    }
  }

}


